// File: oneportal-shell/src/pages/ManageLocations/ManageLocations.tsx

import React, { useEffect, useLayoutEffect, useMemo, useRef, useState } from "react";
import { useTranslation } from "react-i18next";
import { fetchLocations, type ManagedLocation } from "./manageLocationsService";
import { EditLocationModal } from "./EditLocationModal";
import styles from "./ManageLocations.module.css";

type SortMode = "alpha" | "health";

const HEALTH_ORDER: Record<string, number> = { Poor: 0, Fair: 1, Good: 2 };

function formatAddress(loc: ManagedLocation): string {
  const { addressLine1, city, stateProvinceRegionCode, zipPostalCode } = loc.address;
  const cityState = [city, stateProvinceRegionCode].filter(Boolean).join(", ");
  return [addressLine1, [cityState, zipPostalCode].filter(Boolean).join(" ")]
    .filter(Boolean)
    .join(", ");
}

const ManageLocations: React.FC<{ api?: unknown }> = () => {
  const { t } = useTranslation();

  const [locations, setLocations] = useState<ManagedLocation[]>([]);
  const [isLoading, setIsLoading] = useState(true);
  const [loadError, setLoadError] = useState<string | null>(null);
  const [search, setSearch] = useState("");
  const [sortMode, setSortMode] = useState<SortMode>("alpha");
  const [editing, setEditing] = useState<ManagedLocation | null>(null);

  const searchMirrorRef = useRef<HTMLSpanElement>(null);
  const [searchWidth, setSearchWidth] = useState<number | undefined>(undefined);

  useLayoutEffect(() => {
    if (!search) {
      setSearchWidth(undefined);
      return;
    }
    setSearchWidth((searchMirrorRef.current?.offsetWidth ?? 0) + 6);
  }, [search]);

  async function loadLocations() {
    setIsLoading(true);
    setLoadError(null);
    try {
      const data = await fetchLocations();
      setLocations(data);
    } catch {
      setLoadError(t("manageLocations.loadError"));
    } finally {
      setIsLoading(false);
    }
  }

  useEffect(() => {
    loadLocations();
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  const visibleLocations = useMemo(() => {
    const term = search.trim().toLowerCase();
    const filtered = term
      ? locations.filter(
          (loc) =>
            loc.name.toLowerCase().includes(term) ||
            formatAddress(loc).toLowerCase().includes(term),
        )
      : locations;

    const sorted = [...filtered];
    if (sortMode === "alpha") {
      sorted.sort((a, b) => a.name.localeCompare(b.name));
    } else {
      sorted.sort(
        (a, b) =>
          (HEALTH_ORDER[a.healthStatus] ?? 3) - (HEALTH_ORDER[b.healthStatus] ?? 3) ||
          a.name.localeCompare(b.name),
      );
    }
    return sorted;
  }, [locations, search, sortMode]);

  function devicesLabel(loc: ManagedLocation): string {
    if (loc.offlineDevices <= 0) return t("manageLocations.card.allOnline");
    return t("manageLocations.card.offlineCount", { count: loc.offlineDevices });
  }

  function healthPercent(loc: ManagedLocation): number {
    if (loc.totalDevices <= 0) return 100;
    return Math.round((loc.healthyDevices / loc.totalDevices) * 100);
  }

  return (
    <div className={styles.page}>
      <div className={styles.header}>
        <h1 className={styles.pageTitle}>{t("manageLocations.title")}</h1>
      </div>

      {/*Static map placeholder.*/}
      <div className={styles.mapPlaceholder} role="img" aria-label={t("manageLocations.mapAlt")}>
        <span className={styles.mapPlaceholderText}>{t("manageLocations.mapPlaceholder")}</span>
      </div>

      <div className={styles.toolbar}>
        <div className={styles.searchWrapper}>
          <svg
            className={styles.searchIcon}
            width="20"
            height="20"
            viewBox="0 0 20 20"
            fill="none"
            aria-hidden="true"
          >
            <path
              d="M9 3a6 6 0 104.472 10.03l3.249 3.248a.75.75 0 101.06-1.06l-3.248-3.249A6 6 0 009 3zM4.5 9a4.5 4.5 0 119 0 4.5 4.5 0 01-9 0z"
              fill="currentColor"
            />
          </svg>
          <input
            type="text"
            className={`${styles.searchInput} ${search ? styles.searchInputFilled : ""}`}
            style={search ? { width: searchWidth } : undefined}
            placeholder={t("manageLocations.searchPlaceholder")}
            aria-label={t("manageLocations.searchPlaceholder")}
            value={search}
            onChange={(e) => setSearch(e.target.value)}
          />
          {search && (
            <button
              type="button"
              className={styles.searchClear}
              aria-label={t("manageLocations.clearSearch")}
              onClick={() => setSearch("")}
            >
              <svg width="18" height="18" viewBox="0 0 20 20" fill="none" aria-hidden="true">
                <path
                  d="M15 5L5 15M5 5l10 10"
                  stroke="currentColor"
                  strokeWidth="1.75"
                  strokeLinecap="round"
                />
              </svg>
            </button>
          )}
          <span ref={searchMirrorRef} className={styles.searchMirror} aria-hidden="true">
            {search}
          </span>
        </div>

        <select
          className={styles.sortSelect}
          aria-label={t("manageLocations.sortLabel")}
          value={sortMode}
          onChange={(e) => setSortMode(e.target.value as SortMode)}
        >
          <option value="alpha">{t("manageLocations.sortAlpha")}</option>
          <option value="health">{t("manageLocations.sortHealth")}</option>
        </select>
      </div>

      {isLoading ? (
        <p className={styles.stateMessage}>{t("manageLocations.loading")}</p>
      ) : loadError ? (
        <p className={styles.stateMessage}>{loadError}</p>
      ) : visibleLocations.length === 0 ? (
        <div className={styles.emptyState}>
          <p className={styles.emptyTitle}>{t("manageLocations.emptyTitle")}</p>
          <p className={styles.emptySubtitle}>{t("manageLocations.emptySubtitle")}</p>
        </div>
      ) : (
        <div className={styles.grid}>
          {visibleLocations.map((loc) => (
            <div key={loc.id} className={styles.card}>
              <div className={styles.cardTop}>
                <div className={styles.cardTitleBlock}>
                  <p className={styles.cardName}>{loc.name}</p>
                  <p className={styles.cardAddress}>{formatAddress(loc)}</p>
                </div>
                <button
                  type="button"
                  className={styles.editButton}
                  aria-label={t("manageLocations.card.edit")}
                  onClick={() => setEditing(loc)}
                >
                  {t("manageLocations.card.edit")}
                </button>
              </div>

              <div className={styles.metric}>
                <div className={styles.healthHeader}>
                  <span className={styles.metricLabel}>{t("manageLocations.card.health")}</span>
                  <span
                    className={`${styles.healthText} ${
                      styles[`health${loc.healthStatus}`] ?? ""
                    }`}
                  >
                    {loc.healthStatus}
                  </span>
                </div>
                <div className={styles.healthBarTrack}>
                  <div
                    className={`${styles.healthBarFill} ${
                      styles[`healthFill${loc.healthStatus}`] ?? ""
                    }`}
                    style={{ width: `${healthPercent(loc)}%` }}
                  />
                </div>
              </div>

              <div className={styles.cardFooter}>
                <div className={styles.footerCol}>
                  <span className={styles.footerLabel}>{t("manageLocations.card.devices")}</span>
                  <span className={styles.footerValue}>{devicesLabel(loc)}</span>
                </div>
                <div className={styles.footerCol}>
                  <span className={styles.footerLabel}>
                    {t("manageLocations.card.alertsLabel")}
                  </span>
                  <span className={styles.footerValue}>{loc.alertCount}</span>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {editing && (
        <EditLocationModal
          location={editing}
          onClose={() => setEditing(null)}
          onSuccess={() => {
            setEditing(null);
            loadLocations();
          }}
        />
      )}
    </div>
  );
};

export default ManageLocations;
